import { useState, FormEvent, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutDashboard, Mail, User as UserIcon, ArrowLeft, MailCheck } from 'lucide-react';
import { motion } from 'motion/react';
import { useLedgerStore } from '../hooks/use-ledger-store';
import { safeNavigate } from '@/lib/safe-navigate';
import { api } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { ERR } from '../lib/error-messages';

export default function Register() {
  const { register } = useLedgerStore();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState<{ fallbackLink?: string } | null>(null);

  // 若系統未開放註冊，直接跳回登入頁
  useEffect(() => {
    api.auth.registrationStatus()
      .then((r) => {
        if (!r?.allowRegistration) {
          showToast(ERR.AUTH_REGISTRATION_CLOSED);
          safeNavigate('/login');
        }
      })
      .catch(() => safeNavigate('/login'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await register(email, nickname);
      if (result.success) {
        setDone({ fallbackLink: result.fallbackLink });
      } else {
        showToast(ERR.AUTH_REGISTER_FAILED);
      }
    } catch (err: any) {
      showToast(err.message || ERR.GENERIC);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 font-sans">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] bg-primary/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-secondary/10 rounded-full blur-[120px]"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-6 shadow-wash">
            <LayoutDashboard size={32} />
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-foreground mb-2">
            加入家庭
          </h1>
          <p className="text-muted-foreground font-medium">
            建立您的個人帳號以開始分帳
          </p>
        </div>

        <Card className="bg-muted/30 border-border/50 rounded-[2.5rem] p-8 shadow-wash backdrop-blur-xl border border-white/5">
          {done ? (
            /* ── 成功畫面：提示查看信箱並點擊連結 ── */
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <MailCheck size={32} className="text-primary" />
              </div>
              <div className="text-center space-y-2">
                <p className="font-black text-lg">登入連結已寄出</p>
                <p className="text-sm text-muted-foreground">
                  請至 <span className="text-foreground font-semibold">{email}</span> 信箱點擊連結完成首次登入。
                </p>
                {done.fallbackLink && (
                  <p className="text-xs text-muted-foreground mt-4 break-all">
                    （若未收到信，請直接開啟此連結：
                    <a href={done.fallbackLink} className="text-primary underline">{done.fallbackLink}</a>）
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                className="w-full h-12 rounded-2xl font-bold flex items-center justify-center gap-2"
                onClick={() => safeNavigate('/login')}
              >
                <ArrowLeft size={18} />
                <span>返回登入</span>
              </Button>
            </div>
          ) : (
            /* ── 填寫表單 ── */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1">稱呼 (Nickname)</Label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="text"
                    placeholder="如何稱呼您？"
                    className="bg-muted border-transparent focus:border-primary/50 focus:ring-primary/20 rounded-2xl h-14 pl-12"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1">電子郵件</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    className="bg-muted border-transparent focus:border-primary/50 focus:ring-primary/20 rounded-2xl h-14 pl-12"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-black text-lg shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                ) : (
                  '建立帳號'
                )}
              </Button>

              <div className="pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-12 rounded-2xl font-bold flex items-center justify-center gap-2"
                  onClick={() => safeNavigate('/login')}
                >
                  <ArrowLeft size={18} />
                  <span>返回登入</span>
                </Button>
              </div>
            </form>
          )}
        </Card>
      </motion.div>
    </div>
  );
}